import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { InboxItem, Message } from '../fixtures/domain.js';
import type { ApplicationStatus } from '../fixtures/domain.js';
import {
  closingVariantFor,
  hiringOffer,
  isBroadcast,
  isJobBounded,
  type HiringStage,
} from '../fixtures/rules.js';
import type { CandidateProfile, WorkExperience } from '../fixtures/domain.js';
import { Avatar } from '../components/Avatar.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { useVisibleCount } from '../util/useVisibleCount.js';
import { answeredFormsIn } from '../data/forms.js';
import {
  isLocked,
  isLockUnknown,
  isUnlocked,
  unlockDeadline,
  UNLOCK_POLL_MS,
} from '../data/unlockPending.js';
import { FormAnswersSection } from '../form/FormAnswersSection.js';
import { JobDetailsDialog } from './JobDetailsDialog.js';
import { ResumeDialog } from './ResumeDialog.js';
import { NotesSection } from './NotesSection.js';
import { StatusTag } from './StatusTag.js';
import { ENDING_META, STAGE_META } from './stageMeta.js';
import styles from './DetailsPanel.module.css';

/**
 * Marking the jobseeker, and saying where they stand, in one block.
 *
 * The status is the page's live one rather than `item.channel`'s, which lags a
 * stage move by a refetch — a panel offering to shortlist somebody it
 * shortlisted a second ago is what moved this up to the caller. Absent on a chat
 * with no application behind it, which is how the section knows not to draw.
 */
export interface StageControl {
  status: ApplicationStatus;
  /** A write is in flight. */
  busy?: boolean;
  /** The chat has ended, so the outcome stands and can no longer be moved. */
  settled?: boolean;
  onMark: (stage: HiringStage) => void;
  /**
   * End the chat as a rejection. The dialog that drafts the closing message
   * belongs to the conversation pane, this panel's sibling rather than its
   * parent, so this is a request passed up to the page they share.
   */
  onReject: () => void;
}

/**
 * Right-hand context panel for the open conversation. Two tabs: Info (the
 * candidate, their application and the job) and Files (a coming-soon placeholder).
 *
 * Spare, but no longer read-only. It is where the employer has just read the CV,
 * the work history and the form answers, so it is where the decision those
 * support is taken: `stage` puts the pipeline at the top (see `StageControl`).
 * Everything below it stays reference. Job-bounded chats add the Application and
 * Job sections; a direct chat shows the candidate alone, and no pipeline.
 */
export function DetailsPanel({
  item,
  messages,
  loading = false,
  failed = false,
  onRetryDetails,
  onClose,
  resumeOpen,
  onResumeOpenChange,
  stage,
  onReport,
}: {
  item: InboxItem;
  /** The conversation's messages. The notes section reads them to notice when
   *  its summary has gone stale; the contact section reads them to notice that
   *  an unlock is on its way. */
  messages: Message[];
  /** Live details are still being fetched — show skeletons instead of the empty note. */
  loading?: boolean;
  /** The live read failed: Overview says so and offers a retry. */
  failed?: boolean;
  /** Fetch `/details` again. Offered to the employer when a promised unlock
   *  never arrived, so the dead end has a way out that is not a page reload. */
  onRetryDetails?: () => void;
  onClose: () => void;
  /** Whether the résumé dialog is open. Controlled, and deliberately not local:
   *  `?open=resume` opens it on arrival, and only the page reads the URL. */
  resumeOpen: boolean;
  onResumeOpenChange: (open: boolean) => void;
  /** The pipeline, where there is one to draw. See `StageControl`. */
  stage?: StageControl;
  /** Absent where there is nobody to report. */
  onReport?: () => void;
}) {
  const [tab, setTab] = useState<'info' | 'files'>('info');

  return (
    <div className={styles.panel}>
      <header className={styles.header}>
        <div className={styles.tabs} role="tablist" aria-label="Details">
          {(['info', 'files'] as const).map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={tab === id ? styles.tabActive : styles.tab}
              onClick={() => setTab(id)}
            >
              {id === 'info' ? 'Info' : 'Files'}
            </button>
          ))}
        </div>
        {onReport && (
          <button
            type="button"
            className={styles.report}
            onClick={onReport}
            title="Report jobseeker"
            aria-label="Report jobseeker"
          >
            <Icon name="flag" size={13} />
            Report
          </button>
        )}
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close details">
          <Icon name="close" size={20} />
        </button>
      </header>

      {tab === 'info' ? (
        <InfoTab
          item={item}
          messages={messages}
          loading={loading}
          failed={failed}
          onRetryDetails={onRetryDetails}
          resumeOpen={resumeOpen}
          onResumeOpenChange={onResumeOpenChange}
          stage={stage}
        />
      ) : (
        <div className={styles.placeholder} role="tabpanel">
          <EmptyState icon={<Icon name="fileText" size={28} />} title="Coming soon" />
        </div>
      )}
    </div>
  );
}

function InfoTab({
  item,
  messages,
  loading,
  failed,
  onRetryDetails,
  resumeOpen,
  onResumeOpenChange,
  stage,
}: {
  item: InboxItem;
  messages: Message[];
  loading: boolean;
  failed: boolean;
  onRetryDetails?: () => void;
  resumeOpen: boolean;
  onResumeOpenChange: (open: boolean) => void;
  stage?: StageControl;
}) {
  const { counterpart, channel } = item;
  const [jobOpen, setJobOpen] = useState(false);
  const [section, setSection] = useState<InfoSection>('overview');
  const { profile } = counterpart;
  const broadcast = isBroadcast(channel);
  const jobMeta = isJobBounded(channel) ? channel.jobMeta : undefined;
  const facts = profile ? identityFacts(profile) : '';
  // A lens on the thread, not an archive: derived from the loaded messages, so
  // answers leave this panel when retention deletes the chat, by construction.
  const answeredForms = answeredFormsIn(messages);

  // The unlock wait lives here, not in the contact tab: the poll must keep asking
  // while the employer reads Overview, and "stalled" must survive a tab switch.
  const unlocked = profile ? isUnlocked(profile) : true;
  const deadline = unlocked ? undefined : unlockDeadline(messages);
  const waiting = useUnlockWait(deadline);
  useUnlockPoll(waiting === 'waiting' ? deadline : undefined, onRetryDetails);
  // TODO: temporary — no Contact & Resume tab for a locked candidate (the locked card stays, unused).
  const showContact = !!profile && (unlocked || waiting !== 'none');
  const shown = section === 'contact' && !showContact ? 'overview' : section;

  // `?open=resume` lands on Overview; the dialog belongs to the contact tab.
  useEffect(() => {
    if (resumeOpen && showContact) setSection('contact');
  }, [resumeOpen, showContact]);

  return (
    <div className={styles.body} role="tabpanel">
      <div className={styles.identity}>
        <Avatar
          name={counterpart.name}
          src={counterpart.avatarUrl}
          locked={isLocked(counterpart)}
          blurred={(loading || failed) && isLockUnknown(counterpart)}
          size={56}
        />
        <div className={styles.identityMeta}>
          <span data-ph-mask className={styles.name}>
            {counterpart.name}
          </span>
          {facts && <span className={styles.identityFacts}>{facts}</span>}
          {stage && (
            <StatusTag status={stage.status} ended={stage.settled} className={styles.stageTag} />
          )}
        </div>
        {stage && !stage.settled && <StageButtons stage={stage} />}
      </div>

      {/* An Info broadcast has a brand behind it, not a person: nothing to tab through. */}
      {!broadcast && (
        <>
          <div className={styles.subTabs} role="tablist" aria-label="Profile">
            {INFO_SECTIONS.filter(({ id }) => id !== 'contact' || showContact).map(
              ({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={shown === id}
                  className={shown === id ? styles.subTabActive : styles.subTab}
                  onClick={() => setSection(id)}
                >
                  {label}
                </button>
              ),
            )}
          </div>

          {shown === 'overview' && (
            <>
              {failed ? (
                <EmptyState
                  title="Couldn't load these details."
                  {...(onRetryDetails
                    ? { action: { label: 'Try again', onClick: onRetryDetails } }
                    : {})}
                />
              ) : profile ? (
                <CandidateSections profile={profile} />
              ) : loading ? (
                <ProfileSkeleton />
              ) : (
                <p className={styles.emptyNote}>No jobseeker profile available.</p>
              )}

              {jobMeta && (
                <div className={styles.group}>
                  <Section title="Applied to">
                    <div className={styles.job}>
                      <span className={styles.jobTitle}>{jobMeta.jobTitle}</span>
                      <span className={styles.jobMeta}>
                        {[jobMeta.jobType, jobMeta.jobLocation].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    {(jobMeta.description || jobMeta.responsibilities || jobMeta.requirements) && (
                      <button
                        type="button"
                        className={styles.jobButton}
                        onClick={() => setJobOpen(true)}
                      >
                        View job details
                        <Icon name="chevronRight" size={15} />
                      </button>
                    )}
                  </Section>
                </div>
              )}

              {answeredForms.length > 0 && (
                <Section title="Form answers">
                  <div data-ph-mask>
                    <FormAnswersSection answers={answeredForms} />
                  </div>
                </Section>
              )}
            </>
          )}

          {shown === 'contact' && profile && (
            <ContactSection
              profile={profile}
              unlocked={unlocked}
              waiting={waiting}
              onRetryDetails={onRetryDetails}
              onOpenResume={() => onResumeOpenChange(true)}
            />
          )}

          {/* Mounted on every tab so an unsaved note draft survives a look at Overview. */}
          <div hidden={shown !== 'notes'}>
            <Section
              title={
                <>
                  Internal notes
                  <span
                    className={styles.titleHint}
                    title="Only whoever holds this conversation sees this. Neither entry is ever shown to the jobseeker."
                  >
                    <Icon name="lock" size={11} />
                  </span>
                </>
              }
            >
              <NotesSection channelId={channel.id} messages={messages} />
            </Section>
          </div>
        </>
      )}

      {jobOpen && jobMeta && <JobDetailsDialog job={jobMeta} onClose={() => setJobOpen(false)} />}
      {resumeOpen && (
        <ResumeDialog
          channelId={channel.id}
          candidateName={counterpart.name}
          onClose={() => onResumeOpenChange(false)}
        />
      )}
    </div>
  );
}

type InfoSection = 'overview' | 'contact' | 'notes';

const INFO_SECTIONS: { id: InfoSection; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'contact', label: 'Contact & Resume' },
  { id: 'notes', label: 'Notes' },
];

/** The one step forward and the ending; the full pipeline lives in the header. */
function StageButtons({ stage }: { stage: StageControl }) {
  const next = hiringOffer(stage.status).stages[0];
  const ending = ENDING_META[closingVariantFor(true)];
  return (
    <div className={styles.stageButtons}>
      {next && (
        <button
          type="button"
          className={styles.stageButton}
          disabled={stage.busy}
          title={STAGE_META[next].name}
          aria-label={STAGE_META[next].name}
          onClick={() => stage.onMark(next)}
        >
          <Icon name={STAGE_META[next].icon} size={15} filled={false} />
        </button>
      )}
      <button
        type="button"
        className={styles.rejectButton}
        disabled={stage.busy}
        title={ending.label}
        aria-label={ending.label}
        onClick={stage.onReject}
      >
        <Icon name={ending.icon} size={15} />
      </button>
    </div>
  );
}

/** Overview facts are decision inputs, so an absent one says so — a silently
 *  missing row would read as the panel's oversight, not the profile's gap. */
const notProvided = <span className={styles.muted}>Not provided</span>;

/** The "29, Female" line under the name, as the mock writes it: whichever of age
 *  and gender the profile carries; empty (and unrendered) when it carries neither. */
function identityFacts(profile: CandidateProfile): string {
  return [profile.age != null && String(profile.age), profile.gender].filter(Boolean).join(', ');
}

function CandidateSections({ profile }: { profile: CandidateProfile }) {
  // Everything below is unvalidated upstream data — each field may be missing on
  // a dirty record, so every value has somewhere to fall back to.
  const experiences = profile.experiences ?? [];
  const languages = profile.languages ?? [];
  const skills = profile.skills ?? [];
  const preferredLocations = profile.preferredLocations ?? [];

  return (
    <>
      {/* The screening read first, then the history. The sub-tab is the heading. */}
      <Section>
        <Rows>
          <Row label="Expected salary">{profile.expectedSalary ?? notProvided}</Row>
          {/* "Lives in", not "Domicile": the panel speaks plain English
              everywhere else, and "domicile" is a word off an immigration form.
              The wire keeps the formal name; the label is for reading. */}
          {/* City here, full line under Location — as the mock draws it. */}
          <Row label="Lives in">{profile.domicile?.split(',')[0]?.trim() || notProvided}</Row>
          <Row label="Nationality">{profile.nationality ?? notProvided}</Row>
          <Row label="Languages">
            {languages.length > 0 ? <LanguageValue languages={languages} /> : notProvided}
          </Row>
          {/* On screen because it is filterable: a rung an employer can narrow
              the inbox by and then not find on the profile they opened is a
              filter they have to take on faith. */}
          <Row label="Education">{profile.education ?? notProvided}</Row>
        </Rows>
      </Section>

      <div className={styles.group}>
        <Section title="Work experience">
          {experiences.length > 0 ? (
            <div data-ph-mask>
              <ExperienceList experiences={experiences} />
            </div>
          ) : (
            <p className={styles.sectionEmpty}>No working experience</p>
          )}
        </Section>

        <Section title="Skills">
          {skills.length > 0 ? (
            <div data-ph-mask>
              <SkillList skills={skills} />
            </div>
          ) : (
            <p className={styles.sectionEmpty}>No skills listed</p>
          )}
        </Section>
      </div>

      <Section title="Location">
        <Rows>
          <Row label="Lives in">{profile.domicile ?? notProvided}</Row>
          <Row label="Willing to work in">
            {preferredLocations.length > 0 ? preferredLocations.join(', ') : notProvided}
          </Row>
        </Rows>
      </Section>
    </>
  );
}

/**
 * Contact and the résumé, shown only for a candidate this company has unlocked
 * — the BFF omits both fields otherwise, so absence is the whole gate and there
 * is nothing to render around. The unlock affordance itself is not here yet:
 * buying one costs credits and needs an eligibility check the panel cannot make.
 *
 * # Why this section has a waiting state and no other does
 *
 * An unlock is announced to the employer in the thread ("This candidate
 * accepted your chat request") before the fact reaches the row this panel
 * reads — the note is posted on the request that resolved the button, while the
 * unlock itself travels ajt → SNS → SQS → projection. For those seconds the
 * thread says something happened and the panel, rendering nothing, says it did
 * not.
 *
 * That gap is not merely slow, it is misleading: an employer who looks for a
 * phone number, finds no section, and concludes there is none has been given a
 * wrong answer by a panel that simply did not know yet. Holding the place says
 * the true thing — we are expecting this — and it is why the wait is drawn here
 * rather than left to the section appearing quietly later.
 *
 * The wait is derived from the thread and so survives a reload; see
 * `unlockDeadline`. It is bounded, and when it expires under the employer's
 * eyes the section admits it could not load rather than vanishing: a section
 * that appears and then disappears reads as a bug and leaves nothing to act on.
 */
function ContactSection({
  profile,
  unlocked,
  waiting,
  onRetryDetails,
  onOpenResume,
}: {
  profile: CandidateProfile;
  unlocked: boolean;
  waiting: 'none' | 'waiting' | 'stalled';
  onRetryDetails?: () => void;
  onOpenResume: () => void;
}) {
  const { contact, resume } = profile;

  if (!unlocked) {
    if (waiting === 'none') {
      // The mock's locked card, less its button: unlocking costs credits upstream.
      return (
        <Section>
          <div className={styles.locked}>
            <span className={styles.lockBadge}>
              <Icon name="lock" size={22} />
            </span>
            <p className={styles.lockedText}>Unlock to see contact details and send messages.</p>
          </div>
        </Section>
      );
    }
    return waiting === 'stalled' ? <ContactStalled onRetry={onRetryDetails} /> : <ContactPending />;
  }

  return (
    <Section>
      <Rows>
        {contact?.email && (
          <Row label="Email">
            <a className={styles.link} href={`mailto:${contact.email}`}>
              {contact.email}
            </a>
          </Row>
        )}
        {contact?.phone && (
          <Row label="Phone">
            <a className={styles.link} href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`}>
              {contact.phone}
            </a>
          </Row>
        )}
      </Rows>
      {resume && (
        // A button, not a link: there is no URL to point at. The panel is told
        // only that a résumé exists, and the bytes are fetched through our own
        // session when the employer asks — see ResumeDialog.
        <button type="button" className={styles.resume} onClick={onOpenResume}>
          <Icon name="file" size={15} />
          View resume
        </button>
      )}
    </Section>
  );
}

/**
 * Work history. The collapsed view shows the latest role only; the rest of the
 * timeline hides behind the toggle. Which past role best fits the job is a
 * judgement the upstream application data would have to make, not this panel.
 *
 * The toggle counts what is *hidden*, never the total: "See all 2 experiences"
 * above a single visible role reads as a promise of two more, and the click
 * then adds one.
 */
function ExperienceList({ experiences }: { experiences: WorkExperience[] }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? experiences : experiences.slice(0, 1);
  const hidden = experiences.length - 1;
  // The next hidden role, cut off and faded, so the history visibly goes on.
  const peek = expanded ? undefined : experiences[1];

  return (
    <div className={styles.experiences}>
      {shown.map((exp, i) => (
        <ExperienceEntry key={`${exp.company}-${exp.period}-${i}`} experience={exp} />
      ))}
      {peek && <ExperienceEntry experience={peek} peek />}
      {hidden > 0 && (
        <button
          type="button"
          className={styles.link}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded
            ? 'Show less'
            : `Show ${hidden} more ${hidden === 1 ? 'experience' : 'experiences'}`}
        </button>
      )}
    </div>
  );
}

function ExperienceEntry({ experience, peek }: { experience: WorkExperience; peek?: boolean }) {
  return (
    <div
      className={peek ? styles.experiencePeek : styles.experience}
      aria-hidden={peek || undefined}
    >
      <span className={styles.roleTitle}>{experience.title}</span>
      {experience.company && <span className={styles.roleCompany}>{experience.company}</span>}
      <span className={styles.roleMeta}>
        {[experience.period, experience.tenure].filter(Boolean).join(' · ')}
      </span>
    </div>
  );
}

/** How many lines the collapsed Languages row is allowed to occupy. */
const LANGUAGE_LINES = 2;

/** How many skills show before the rest collapse behind the toggle. */
const SKILL_LIMIT = 6;

/**
 * Languages as a comma-separated run clamped to two lines, with the overflow
 * behind a toggle on its own line below.
 *
 * CSS can clamp the lines but can't say which names were cut, so the count comes
 * from useVisibleCount replaying the wrap.
 */
function LanguageValue({ languages }: { languages: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const [namesRef, fits] = useVisibleCount<HTMLSpanElement>(LANGUAGE_LINES, languages.join(' '));

  // Everything shows until the measure settles (see `useVisibleCount`).
  const visible = fits ?? languages.length;
  const hidden = languages.length - visible;
  const shown = expanded ? languages : languages.slice(0, visible);

  return (
    <span className={styles.languages}>
      <span ref={namesRef} className={styles.languageNames}>
        {shown.map((language, i) => (
          <span key={language} className={styles.language}>
            {i < shown.length - 1 ? `${language},` : language}
          </span>
        ))}
      </span>
      {hidden > 0 && (
        <button
          type="button"
          className={styles.languageToggle}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : `Show ${hidden} more`}
        </button>
      )}
    </span>
  );
}

/** Skills as pills. SKILL_LIMIT is a preview cap, not a height clamp — six long
 *  names still wrap to several rows — so unlike Languages this one is counted
 *  rather than measured. The measure doesn't apply here anyway: the toggle rides
 *  inside the run as a pill, which is the case `wrap.ts` declines. Each pill
 *  truncates a very long single skill rather than blowing out the width. */
function SkillList({ skills }: { skills: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const overflow = skills.length - SKILL_LIMIT;
  const shown = expanded ? skills : skills.slice(0, SKILL_LIMIT);

  return (
    <div className={styles.skills}>
      {shown.map((skill) => (
        <span key={skill} className={styles.skill} title={skill}>
          {skill}
        </span>
      ))}
      {overflow > 0 && (
        <button
          type="button"
          className={styles.skillMore}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less' : `+${overflow} more`}
        </button>
      )}
    </div>
  );
}

/** Fallback for the ErrorBoundary around the panel: keeps the panel chrome (so
 *  the user can still close it) but replaces the body with a plain note when a
 *  render throws on dirty data. */
export function DetailsPanelError({ onClose }: { onClose: () => void }) {
  return (
    <div className={styles.panel}>
      <header className={styles.header}>
        <span className={styles.name}>Details</span>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close details">
          <Icon name="close" size={20} />
        </button>
      </header>
      <div className={styles.body}>
        <p className={styles.emptyNote}>Couldn&apos;t load these details.</p>
      </div>
    </div>
  );
}

/** Shimmer placeholder while live candidate data loads — same treatment as the
 *  quick-replies skeleton, so loading reads as one language across the app. */
function ProfileSkeleton() {
  return (
    <div className={styles.skeletons} aria-hidden="true">
      <span className={styles.skeleton} />
      <span className={styles.skeleton} />
      <span className={styles.skeleton} />
      <span className={styles.skeleton} />
    </div>
  );
}

/**
 * Which of the three things to say about a promised unlock: nothing, that we
 * are waiting, or that it never arrived.
 *
 * The one thing a deadline cannot tell us on its own is whether the employer
 * ever saw the wait. That matters, because the failure state exists to avoid
 * taking back a section they were already shown — open a months-old thread
 * whose unlock silently failed and there is nothing to take back, nothing "Try
 * again" can fix, and no reason to raise an alarm about it now. So a deadline
 * already in the past when this panel arrived reads as `none`, while the same
 * deadline passing under the employer's eyes reads as `stalled`.
 *
 * The expiry is scheduled rather than waited for, because a render alone cannot
 * notice time passing and the events that would re-render this panel are
 * exactly the ones that may never come.
 */
function useUnlockWait(deadline: number | undefined): 'none' | 'waiting' | 'stalled' {
  const [, tick] = useState(0);
  const sawWaiting = useRef(false);

  const waiting = deadline !== undefined && Date.now() < deadline;

  useEffect(() => {
    if (waiting) sawWaiting.current = true;
  }, [waiting]);

  useEffect(() => {
    if (deadline === undefined) return;
    const remaining = deadline - Date.now();
    if (remaining <= 0) return;
    const timer = setTimeout(() => tick((n) => n + 1), remaining);
    return () => clearTimeout(timer);
  }, [deadline]);

  if (waiting) return 'waiting';
  return sawWaiting.current ? 'stalled' : 'none';
}

/**
 * Re-read `/details` on a timer for as long as `deadline` is open, so the
 * section fills itself in instead of waiting to be told.
 *
 * The socket announcement it backs up (`data_changed`, see `installLiveClient`)
 * is an optimization and not a delivery guarantee: Stream neither persists nor
 * pushes custom events, so one emitted while the employer's socket is between
 * reconnects — or not emitted at all — is simply lost, and the panel had no
 * second path to the fact. What that looked like is the whole reason this
 * exists: the spinner ran the full window, the section admitted defeat, and
 * "Try again" then worked on the first press. The data had been there for
 * seconds; only the nudge was missing.
 *
 * So the nudge stops depending on the socket. The announcement still shortens
 * the wait when it does arrive — it is no longer the only thing that can end
 * it.
 *
 * Bounded by the same deadline the spinner is, and only ever open on a
 * conversation the employer has on screen, so a thread nobody is waiting on
 * polls nothing and a genuinely failed unlock still settles into the stalled
 * state rather than hammering the route forever.
 */
function useUnlockPoll(deadline: number | undefined, refetch?: () => void): void {
  // Held in a ref so a caller that rebuilds the callback each render doesn't
  // restart the interval — which would push the next poll out indefinitely and
  // quietly turn this back into the thing it replaces.
  const latest = useRef(refetch);
  useEffect(() => {
    latest.current = refetch;
  }, [refetch]);

  const canRefetch = Boolean(refetch);

  useEffect(() => {
    if (deadline === undefined || !canRefetch || Date.now() >= deadline) return;
    const timer = setInterval(() => {
      // This timer and the expiry one race on the last tick; never fetch past
      // the window the spinner is promising.
      if (Date.now() >= deadline) return;
      latest.current?.();
    }, UNLOCK_POLL_MS);
    return () => clearInterval(timer);
  }, [deadline, canRefetch]);
}

/**
 * The held place. Titled exactly as the real section is, so nothing moves when
 * the rows arrive — only the contents change.
 *
 * `aria-live="polite"` because this is the one section that resolves itself
 * while being read: a screen reader user who has already passed it gets told
 * the details landed, rather than having to go back and check.
 */
function ContactPending() {
  return (
    <Section>
      <p className={styles.pending} aria-live="polite">
        <span className={styles.spinner} aria-hidden="true" />
        Unlocking contact details…
      </p>
    </Section>
  );
}

/** The wait ran out. Says what is true — this did not load — and offers the
 *  scoped retry, rather than leaving a page reload as the only move. */
function ContactStalled({ onRetry }: { onRetry?: () => void }) {
  return (
    <Section>
      <p className={styles.pending} aria-live="polite">
        Couldn&rsquo;t load contact details.
      </p>
      {onRetry && (
        <button type="button" className={styles.resume} onClick={onRetry}>
          Try again
        </button>
      )}
    </Section>
  );
}

function Section({ title, children }: { title?: ReactNode; children: ReactNode }) {
  return (
    <section className={styles.section}>
      {title && <h3 className={styles.sectionTitle}>{title}</h3>}
      {children}
    </section>
  );
}

/** The shared label/value grid. Rows lay out only inside it: a `.row` is
 *  `display: contents`, so a bare Row dissolves into whatever container its
 *  parent happens to be and stacks its label over its value. Pairing the two as
 *  components makes that mistake unwritable. */
function Rows({ children }: { children: ReactNode }) {
  return <div className={styles.rows}>{children}</div>;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      {/* VALUES ARE MASKED IN A SESSION REPLAY, LABELS ARE NOT (AI-3016). Every
          candidate fact in this panel arrives through here — where they live,
          what they earn, their email and phone — so masking the value once
          covers all of them, and a row added later is masked without anyone
          remembering to. The label stays readable, which is what keeps a replay
          legible: you can see WHICH row was read without reading it. */}
      <span data-ph-mask className={styles.rowValue}>
        {children}
      </span>
    </div>
  );
}
